import { routing } from "@/i18n/routing";

/**
 * Canonical site URL — the public face of the site, used for sitemap,
 * robots, OpenGraph URLs, hreflang and JSON-LD.
 *
 * Hard-coded to the production custom domain on purpose. Vercel injects
 * `NEXT_PUBLIC_SITE_URL=https://isg-express.vercel.app` (the platform-
 * assigned `.vercel.app` URL) into the build environment, which is the
 * wrong canonical for SEO. We opt out and use the custom domain
 * directly — overridable via `NEXT_PUBLIC_CANONICAL_URL` if ever needed.
 */
export const SITE_URL =
  process.env.NEXT_PUBLIC_CANONICAL_URL ?? "https://www.isg-express.com";

/**
 * Build an absolute URL for a path in a given locale, respecting next-intl's
 * `localePrefix: "as-needed"` setting — `de` (the default) has no prefix,
 * every other locale lives under `/<locale>`.
 *
 *   localizedUrl("de", "/")            // https://…/
 *   localizedUrl("de", "/standorte")   // https://…/standorte
 *   localizedUrl("en", "/")            // https://…/en
 *   localizedUrl("fr", "/standorte")   // https://…/fr/standorte
 */
export function localizedUrl(locale: string, path: string): string {
  const seg = path === "/" ? "" : path;
  if (locale === routing.defaultLocale) {
    return `${SITE_URL}${seg || "/"}`;
  }
  return `${SITE_URL}/${locale}${seg}`;
}

/**
 * The standalone booking app. The booking flow now lives here (the flow is at
 * the **root** of this domain), so every "Transport buchen" CTA + the
 * `/angebot` redirect points at it. Constant on purpose — the value is fixed
 * and we don't want a Vercel-injected `NEXT_PUBLIC_*` overriding it.
 */
export const APP_URL = "https://app.isg-express.com";

/**
 * Build an absolute URL into the booking app for a given locale, respecting the
 * same `as-needed` prefix as {@link localizedUrl}. The flow sits at the app
 * root, so `path` defaults to "" (root).
 *
 *   appLocalizedUrl("de")   // https://app.isg-express.com/
 *   appLocalizedUrl("en")   // https://app.isg-express.com/en
 */
export function appLocalizedUrl(locale: string, path = ""): string {
  const seg = path === "/" ? "" : path;
  if (locale === routing.defaultLocale) {
    return `${APP_URL}${seg || "/"}`;
  }
  return `${APP_URL}/${locale}${seg}`;
}

/**
 * Map an internal routing locale to the `hreflang` code used in sitemap +
 * `<link rel="alternate">` tags. Identity for everything except `zh` →
 * `zh-Hans`: Google's recommendation is to use BCP-47 region/script tags;
 * bare `zh` is generic and weaker for ranking (Chinese has Simplified vs
 * Traditional variants). Our site targets Simplified Chinese audiences
 * (Festland-China business), so `zh-Hans` is correct. The URL segment
 * stays `/zh/...` — only the hreflang code shifts.
 *
 * Returning the original locale for anything else keeps single-language
 * codes (`de`, `en`, `fr`, …) which Google treats as language-only
 * (matches all regions for that language). That's what we want for
 * everything except Chinese.
 */
export function hreflangFor(locale: string): string {
  if (locale === "zh") return "zh-Hans";
  return locale;
}
